import React, { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useTranslation } from 'react-i18next';
import { Divider } from 'primereact/divider';
import styles from './DeregistrationDialog.styles.scss';
import cn from 'classnames';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { FormikField } from '../../../../components/FormikField';
import DropDownField from '../../../../components/FormikFields/DropDownField';
import { InputField } from '../../../../components/FormikFields';

const DeregistrationDialog = ({ isOpen, setIsOpen, handleSubmit }) => {
  const { t } = useTranslation();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formValues, setFormValues] = useState();

  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setIsSubmitted(false);
      }, 200);
    }
  }, [isOpen]);

  return (
    <Dialog
      className={cn('rounded-4 overflow-hidden', styles.DeregistrationDialog)}
      visible={isOpen}
      onHide={() => {
        setIsOpen(false);
      }}
    >
      {!isSubmitted && (
        <div className={styles.submissionMessage}>
          <div className={cn(styles.icon, styles.imageDiv)}>
            <div className={styles.imgDivBg}>
              <i className="pi pi-file-excel" />
            </div>
          </div>
          <h3 className="mb-5">{t('solution_deregister.deregistration')}</h3>
          <Formik
            validateOnMount
            enableReinitialize
            validationSchema={Yup.object().shape({
              reasonOption: Yup.string()
                .required(t('solution_deregister.please-select-a-reason'))
                .oneOf(
                  t('solution_deregister.options', {
                    returnObjects: true,
                  }).map((k) => k.value),
                ),
              reasonDesc: Yup.string()
                .max(
                  50,
                  t(
                    'solution_deregister.please-enter-a-maximum-of-50-characters',
                  ),
                )
                .when('reasonOption', {
                  is: 'OTHER',
                  then: Yup.string().required(
                    t(
                      'solution_deregister.please-enter-a-deregistration-reason',
                    ),
                  ),
                }),
            })}
            initialValues={{
              reasonOption: '',
              reasonDesc: '',
            }}
            onSubmit={() => {
              setIsSubmitted(true);
            }}
          >
            {({ values, errors, touched, setFieldValue, setTouched }) => {
              return (
                <Form className="d-flex gap-4 flex-column w-100">
                  <div className="px-4 gap-4 d-flex flex-column">
                    <FormikField
                      name={'reasonOption'}
                      tooltip={t('tooltips.dergister-reason-select')}
                      label={t(
                        'solution_deregister.please-provide-the-reason-for-deregistration',
                      )}
                      required
                      component={DropDownField}
                      error={errors.reasonOption}
                      componentProps={{
                        options: t('solution_deregister.options', {
                          returnObjects: true,
                        }),
                        placeholder: t('solution_deregister.select-reason'),
                        className: cn(
                          'w-100',
                          !values.reasonOption ? styles.emptyField : '',
                          errors.reasonOption && touched.reasonOption
                            ? styles.errorField
                            : '',
                        ),
                      }}
                    />
                    {values.reasonOption === 'OTHER' && (
                      <FormikField
                        name={'reasonDesc'}
                        component={InputField}
                        label={t('solution_deregister.reason')}
                        required
                        error={errors.reasonDesc}
                        componentProps={{
                          placeholder: t(
                            'solution_deregister.provide-the-reason-for-deregistration',
                          ),
                          className: cn(
                            !values.reasonDesc ? styles.emptyField : '',
                            errors.reasonDesc && touched.reasonDesc
                              ? styles.errorField
                              : '',
                          ),
                        }}
                      />
                    )}
                  </div>
                  <Divider className="mb-2" />
                  <div className="d-flex gap-4 px-4 pb-4">
                    <Button
                      onClick={() => {
                        setIsOpen(false);
                      }}
                      type="button"
                      className="px-5 flex-grow-1"
                      size="medium"
                      outlined
                      label={t('common.cancel')}
                    />
                    <Button
                      onClick={() => {
                        setFormValues(values);
                        setIsSubmitted(true);
                      }}
                      disabled={Object.keys(errors).length > 0}
                      loading={isLoading}
                      type="submit"
                      className="px-5 flex-grow-1"
                      size="medium"
                      label={t('common.next')}
                    />
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      )}
      {isSubmitted && (
        <div className={styles.submissionMessage}>
          <div className={cn(styles.icon, styles.imageDiv)}>
            <div className={styles.imgDivBg}>
              <i className="pi pi-file-excel" />
            </div>
          </div>
          <h3>
            {t(
              'solution_deregister.are-you-sure-you-want-to-deregister-the-device-s',
            )}
          </h3>
          <p>
            {t(
              'solution_deregister.upon-deregistration-the-device-s-will-no-longer-be-able-to-generate-e-invoices',
            )}
          </p>
          <Divider className="mb-4" />
          <div className="d-flex gap-4 px-4 pb-4">
            <Button
              onClick={() => {
                setIsOpen(false);
              }}
              type="button"
              className="px-5 flex-grow-1"
              size="medium"
              outlined
              label={t('common.cancel')}
            />
            <Button
              onClick={async () => {
                setIsLoading(true);
                handleSubmit({
                  reason: formValues?.reasonOption,
                  description: formValues?.reasonDesc,
                })
                  .then(() => {
                    setIsOpen(false);
                  })
                  .finally(() => {
                    setIsLoading(false);
                  });
              }}
              loading={isLoading}
              type="submit"
              className="px-5 flex-grow-1"
              size="medium"
              label={t('common.confirm')}
            />
          </div>
        </div>
      )}
    </Dialog>
  );
};

export default React.memo(DeregistrationDialog);
